// ============================================================
// useDashboardCliente.ts
// Lógica de carga del dashboard del cliente.
// - Carga estadísticas
// - Carga próximas citas
// - Cambio de meses
// - Loading y manejo de errores
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';

/* ---------- Tipos ---------- */

export interface DashboardStats {
  citasHoy: number;
  citasMes: number;
  gastoMes: number;
  gastoTotal: number;
  cancelacionesMes: number;
  valoracionesPendientes: number;
}

export interface ProximaCita {
  id_cita: string;
  nombre_empresa: string;
  nombre_servicio: string;
  fecha_cita: string;
  hora_cita: string;
  hora_fin: string;
  estado_cita: string;
  precio: number;
}

/** Fila que devuelve la RPC get_dashboard_cliente. El cliente de Supabase no
 *  conoce su tipo (no está en los tipos generados), así que sin este cast
 *  `data` sale como `{}` y tsc se queja de cada propiedad al leerla. */
interface DashboardClienteRow {
  citas_hoy: number;
  citas_mes: number;
  gasto_mes: number;
  gasto_total: number;
  cancelaciones_mes: number;
  valoraciones_pendientes: number;
  proximas_citas: ProximaCita[];
}

/* ---------- Constantes ---------- */

const STATS_INICIALES: DashboardStats = {
  citasHoy: 0,
  citasMes: 0,
  gastoMes: 0,
  gastoTotal: 0,
  cancelacionesMes: 0,
  valoracionesPendientes: 0,
};

/* ============================================================
   HOOK PRINCIPAL
============================================================ */

export const useDashboardCliente = (email: string | null) => {
  const ahora = new Date();

  const [mes, setMes] = useState(ahora.getMonth() + 1);
  const [anio, setAnio] = useState(ahora.getFullYear());

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>(STATS_INICIALES);
  const [proximasCitas, setProximasCitas] = useState<ProximaCita[]>([]);

  /* ---------- Fetch ---------- */

  const fetchDashboard = useCallback(async () => {
    if (!email) return;

    setLoading(true);

    const { data: raw, error } = await supabase
      .rpc('get_dashboard_cliente', { p_email: email, p_mes: mes, p_anio: anio })
      .maybeSingle();

    const data = raw as DashboardClienteRow | null;

    if (error) {
      Sentry.captureException(error, { extra: { email, mes, anio } }); // 👈
      setLoading(false);
      return;
    }

    setStats({
      citasHoy: data?.citas_hoy ?? 0,
      citasMes: data?.citas_mes ?? 0,
      gastoMes: Number(data?.gasto_mes ?? 0),
      gastoTotal: Number(data?.gasto_total ?? 0),
      cancelacionesMes: data?.cancelaciones_mes ?? 0,
      valoracionesPendientes: data?.valoraciones_pendientes ?? 0,
    });

    setProximasCitas(data?.proximas_citas ?? []);
    setLoading(false);
  }, [email, mes, anio]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  /* ---------- Navegación meses ---------- */

  const mesAnterior = () => {
    if (mes === 1) setAnio((a) => a - 1);
    setMes((m) => (m === 1 ? 12 : m - 1));
  };
  const mesSiguiente = () => {
    if (mes === 12) setAnio((a) => a + 1);
    setMes((m) => (m === 12 ? 1 : m + 1));
  };
  const anioAnterior = () => setAnio((a) => a - 1);
  const anioSiguiente = () => setAnio((a) => a + 1);

  /* ---------- Meses visibles ---------- */

  const obtenerMesesVisibles = () => {
    const indices: number[] = [];
    for (let offset = -2; offset <= 2; offset++) {
      indices.push((mes - 1 + offset + 12) % 12);
    }
    return indices;
  };

  return {
    loading,
    stats,
    proximasCitas,
    mes,
    anio,
    setMes,
    setAnio,
    mesAnterior,
    mesSiguiente,
    anioAnterior,
    anioSiguiente,
    obtenerMesesVisibles,
  };
};